import { lazy, Suspense, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import { store } from '@/store/store';
import type { AppDispatch } from '@/store/store';
import { AppThemeProvider } from '@/theme/ThemeProvider';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/components/auth/LoginPage';
import { useAuth } from '@/hooks/useAuth';
import { setCredentials } from '@/store/slices/authSlice';
import { getAccessToken, getRefreshToken, parseTokenPayload, isTokenExpired } from '@/utils/jwt';
import type { ReactNode } from 'react';
import type { User } from '@/types';
import { SymbioteSocketProvider } from '@/hooks/useWebSocket';

/**
 * Hydrates Redux auth state from localStorage tokens on app startup.
 * Uses a ref to run exactly once during first render (no useEffect needed).
 */
function AuthInitializer({ children }: { children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const hydrated = useRef<boolean | null>(null);

  if (hydrated.current == null) {
    hydrated.current = true;

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (accessToken && !isTokenExpired(accessToken)) {
      const payload = parseTokenPayload(accessToken);
      if (payload) {
        const user: User = {
          id: typeof payload.sub === 'string' ? payload.sub : '',
          email: typeof payload.email === 'string' ? payload.email : '',
          displayName:
            typeof payload.name === 'string'
              ? payload.name
              : typeof payload.email === 'string'
                ? payload.email
                : '',
          roles: Array.isArray(payload.roles) ? (payload.roles as string[]) : [],
        };
        dispatch(setCredentials({ user, accessToken, refreshToken: refreshToken ?? '' }));
      }
    }
  }

  return <>{children}</>;
}

const ChatPage = lazy(() =>
  import('@/components/chat/ChatPage').then((m) => ({ default: m.ChatPage })),
);
const AgentStudioPage = lazy(() =>
  import('@/components/agent-studio/AgentStudioPage').then((m) => ({ default: m.AgentStudioPage })),
);
const PipelinesPage = lazy(() =>
  import('@/components/pipelines/PipelinesPage').then((m) => ({ default: m.PipelinesPage })),
);
const PipelineEditorPage = lazy(() =>
  import('@/components/pipelines/PipelineEditorPage').then((m) => ({
    default: m.PipelineEditorPage,
  })),
);
const MonitoringPage = lazy(() =>
  import('@/components/monitoring/MonitoringPage').then((m) => ({ default: m.MonitoringPage })),
);
const KnowledgePage = lazy(() =>
  import('@/components/knowledge/KnowledgePage').then((m) => ({ default: m.KnowledgePage })),
);
const ReviewQueue = lazy(() =>
  import('@/components/knowledge/ReviewQueue').then((m) => ({ default: m.ReviewQueue })),
);
const AdminPage = lazy(() =>
  import('@/components/admin/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const GoogleDriveCallback = lazy(() =>
  import('@/components/connectors/GoogleDriveCallback').then((m) => ({
    default: m.GoogleDriveCallback,
  })),
);
const JobsPage = lazy(() =>
  import('@/components/jobs/JobsPanel').then((m) => ({ default: m.JobsPanel })),
);
const QcPage = lazy(() => import('@/components/qc/QcPage').then((m) => ({ default: m.QcPage })));
const MicroagentsPage = lazy(() =>
  import('@/pages/MicroagentsPage').then((m) => ({ default: m.MicroagentsPage })),
);
function PageLoader() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <CircularProgress sx={{ color: 'primary.main' }} />
    </Box>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <AuthInitializer>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/chat" replace />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="chat/:conversationId" element={<ChatPage />} />
              <Route path="agent-studio" element={<AgentStudioPage />} />
              <Route path="pipelines" element={<PipelinesPage />} />
              <Route path="pipelines/:templateId" element={<PipelineEditorPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="knowledge/reviews" element={<ReviewQueue />} />
              <Route path="knowledge" element={<KnowledgePage />} />
              <Route path="knowledge/:collectionId" element={<KnowledgePage />} />
              <Route path="rules" element={<Navigate to="/admin/rules" replace />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="admin/:tab" element={<AdminPage />} />
              <Route path="connectors" element={<Navigate to="/admin/connectors" replace />} />
              <Route path="connectors/google-drive/callback" element={<GoogleDriveCallback />} />
              <Route path="actions" element={<Navigate to="/admin/actions" replace />} />
              <Route path="jobs" element={<JobsPage />} />
              <Route path="qc" element={<QcPage />} />
              <Route path="microagents" element={<MicroagentsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthInitializer>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <SymbioteSocketProvider>
        <AppThemeProvider>
          <AppRoutes />
        </AppThemeProvider>
      </SymbioteSocketProvider>
    </Provider>
  );
}
